import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, RefreshCw, Send } from 'lucide-react';
import { waysApi, type WaysMessage } from '../../services/waysApi';
import { hasSignedIn, signIn, WaysSignInRequired } from '../../services/waysAuth';
import { useAuthStore } from '../../store/useAuthStore';
import { MessageRow } from '../../pages/WaysPage';

/**
 * PO-92 F5.1 S5.1.2: what Ways has said about one work item -- messages whose work-item chain
 * includes it or one of its descendants, across the channels the person may read. Loaded only
 * when shown; Ways decides what is readable. A note written here is tagged with the item and goes
 * to its epic's channel (PO46-1591): Ways places it from `targetWork`, never a channel PMO guesses.
 */
export default function WaysConversation({ itemId, projectId }: { itemId: string; projectId?: string }) {
  const email = useAuthStore((s) => s.user?.email);
  const [messages, setMessages] = useState<WaysMessage[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [signedOut, setSignedOut] = useState(!hasSignedIn());
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await waysApi.conversation(itemId);
      setMessages(r.messages);
      setError(null);
    } catch (e) {
      if (e instanceof WaysSignInRequired) setSignedOut(true);
      else setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  const send = async () => {
    const text = draft.trim();
    if (!text || !projectId) return;
    setSending(true);
    try {
      await waysApi.send({ kind: 'note', to: [{ kind: 'project', id: projectId }], targetWork: itemId, work: itemId, text });
      setDraft('');
      await load();
    } catch (e) {
      if (e instanceof WaysSignInRequired) setSignedOut(true);
      else setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    setDraft('');
    setMessages(null);
    if (!signedOut) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, signedOut]);

  if (signedOut) {
    return (
      <div className="px-4 pb-4 text-sm text-gray-500">
        <button type="button" className="text-accent hover:underline"
          onClick={() => signIn(email, window.location.pathname + window.location.search)}>
          Sign in to Ways
        </button>{' '}to see what people and agents have said about this item.
      </div>
    );
  }
  return (
    <div className="pb-3">
      <div className="flex items-center justify-between px-4 pb-1 text-xs text-gray-500">
        <span>{messages ? `${messages.length} message(s)` : ''}</span>
        <span className="flex items-center gap-2">
          <Link to="/ways" className="hover:text-accent">Open Ways</Link>
          <button type="button" onClick={load} title="Refresh" className="hover:text-accent">
            <RefreshCw size={12} />
          </button>
        </span>
      </div>
      {error ? <p className="px-4 text-sm text-red-600 dark:text-red-400">{error}</p> : null}
      {loading && !messages ? (
        <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-gray-400" /></div>
      ) : messages && !messages.length ? (
        <p className="px-4 text-sm text-gray-500">Nothing in Ways about this item yet.</p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-800">
          {(messages || []).map((m) => <MessageRow key={m.id} m={m} />)}
        </ul>
      )}
      {projectId ? (
        <form className="flex items-end gap-2 px-4 pt-2" onSubmit={(e) => { e.preventDefault(); send(); }}>
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2}
            aria-label="Write about this item in Ways"
            placeholder="Write about this item… (tagged with it, posted in its epic's channel)"
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); } }}
            className="flex-1 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1" />
          <button type="submit" disabled={sending || !draft.trim()} title="Send (Ctrl+Enter)"
            className="p-2 rounded-md bg-accent text-white disabled:opacity-50">
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          </button>
        </form>
      ) : null}
    </div>
  );
}
